import { Download } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";

interface Props {
  onClick: () => void;
  /** True while the underlying mutation is in flight. Renders as
   *  "Generating…" and disables the button. */
  pending: boolean;
  /** Caller-specified gate (e.g. "no completed revision yet"). The
   *  button is disabled when this is true OR when `pending`. */
  disabled?: boolean;
  /** Override the default "Download PDF" label, e.g. "Download Proposal".
   *  The pending state always renders "Generating…" regardless. */
  label?: string;
  /** `type="button"` is the default — same as everywhere else in the
   *  app. Override to `"submit"` only when rendered inside a form
   *  whose primary action is to download. */
  type?: "button" | "submit";
}

/**
 * Canonical "Download PDF" button. Use this anywhere a server-rendered
 * PDF report is downloaded (proposal, bill analyzer, future reports).
 *
 * Wrap a `useMutation` that calls `api.download(...)` from
 * `@/lib/api`; pass the mutation's `isPending` as `pending` and call
 * `mutation.mutate(...)` from `onClick`. Error handling stays at the
 * call site so each surface can decide between `toast.error` and
 * `mapServerErrors`.
 *
 * Visual contract: default `Button` variant (primary teal), default
 * size, `Download` icon at 12px. Matches the proposal tab's existing
 * button so PDF download surfaces feel identical across the app.
 */
export const DownloadPdfButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ onClick, pending, disabled = false, label = "Download PDF", type = "button" }, ref) => (
    <Button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={pending || disabled}
    >
      <Download size={12} />
      {pending ? "Generating…" : label}
    </Button>
  ),
);
DownloadPdfButton.displayName = "DownloadPdfButton";
